/**
 * Machine Setup Store
 * Zustand store for managing the machine creation wizard state
 * Follows Clean Architecture - all API calls through servicesContainer use cases
 */

import { create } from 'zustand';
import { servicesContainer } from '@/core/di/services-container';
import type {
  MachineType,
  WizardData,
  CreateMachineResponse,
  SerialValidationResponse,
  ValidateMachineConfigResponse,
  BulkCreateMachineRequest,
  BulkCreateMachineResponse,
  MachineGroupsResponse,
  LynxMachineInfoResponse,
  ApplyAttributeItem,
  MachineTypeSettingsResponse,
  SaveTypeSettingsRequest,
  AddCustomMachineTypeRequest,
  CreateThirdPartyMachineRequest,
  ThirdPartyManufacturer,
  DeviceSuggestion,
} from '../types/machine-setup-types';
import { MachineSetupStep, MachineSetupStep as Step } from '../types/machine-setup-types';

interface MachineSetupStore {
  // State
  currentStep: MachineSetupStep;
  machineTypes: MachineType[];
  wizardData: Partial<WizardData>;
  serialValidation: SerialValidationResponse | null;
  creationResult: CreateMachineResponse | null;
  bulkCreateResult: BulkCreateMachineResponse | null;
  machineValidationResult: ValidateMachineConfigResponse | null;
  error: string | null;

  // Granular loading flags
  isLoadingTypes: boolean;
  isValidatingSerial: boolean;
  isCreating: boolean;
  isCreatingBulk: boolean;
  isValidatingConfig: boolean;
  isLoadingMachineGroups: boolean;
  isLoadingActors: boolean;
  isLoadingDevices: boolean;

  // Data-lookup loading flags
  isLoadingTypeSettings: boolean;
  isSavingTypeSettings: boolean;
  isAddingCustomType: boolean;
  isDeletingCustomType: boolean;
  isLoadingManufacturers: boolean;
  isLoadingMachineInfo: boolean;
  isApplyingAttributes: boolean;
  isCreatingThirdParty: boolean;

  // Data-lookup error fields
  typeSettingsError: string | null;
  saveTypeSettingsError: string | null;

  // Actions — wizard core
  loadMachineTypes: () => Promise<void>;
  validateSerial: (serialNumber: string) => Promise<void>;
  createMachine: () => Promise<void>;
  createMachineBulk: (request: BulkCreateMachineRequest) => Promise<void>;
  validateMachineConfig: (machineId: number, machineTypeKey: string) => Promise<void>;

  // Actions — data lookups (return values for local component use)
  loadMachineGroups: (actorId: number) => Promise<MachineGroupsResponse>;
  loadActorsForOperator: (operatorId: number) => Promise<Array<{ actorId: number; name: string; parentActorId?: number }>>;
  loadDevicesForActor: (actorId: number) => Promise<DeviceSuggestion[]>;
  getMachineInfo: (machineId: number) => Promise<LynxMachineInfoResponse>;
  applyAttributes: (machineId: number, attributes: ApplyAttributeItem[]) => Promise<{ isSuccess: boolean; message: string | null }>;
  loadTypeSettings: () => Promise<MachineTypeSettingsResponse>;
  saveTypeSettings: (key: string, settings: SaveTypeSettingsRequest) => Promise<{ isSuccess: boolean; errorMessage?: string }>;
  addCustomMachineType: (data: AddCustomMachineTypeRequest) => Promise<{ isSuccess: boolean; errorMessage?: string }>;
  deleteCustomMachineType: (key: string) => Promise<{ isSuccess: boolean; errorMessage?: string }>;
  loadManufacturers: () => Promise<ThirdPartyManufacturer[]>;
  createThirdPartyMachine: (data: CreateThirdPartyMachineRequest) => Promise<CreateMachineResponse>;

  // Navigation
  setStep: (step: MachineSetupStep) => void;
  setWizardData: (data: Partial<WizardData>) => void;
  clearSerialValidation: () => void;
  reset: () => void;
}

const initialState = {
  currentStep: Step.MachineType as MachineSetupStep,
  machineTypes: [],
  wizardData: {},
  serialValidation: null,
  creationResult: null,
  bulkCreateResult: null,
  machineValidationResult: null,
  error: null,
  isLoadingTypes: false,
  isValidatingSerial: false,
  isCreating: false,
  isCreatingBulk: false,
  isValidatingConfig: false,
  isLoadingMachineGroups: false,
  isLoadingActors: false,
  isLoadingDevices: false,
  isLoadingTypeSettings: false,
  isSavingTypeSettings: false,
  isAddingCustomType: false,
  isDeletingCustomType: false,
  isLoadingManufacturers: false,
  isLoadingMachineInfo: false,
  isApplyingAttributes: false,
  isCreatingThirdParty: false,
  typeSettingsError: null,
  saveTypeSettingsError: null,
};

export const useMachineSetupStore = create<MachineSetupStore>((set, get) => ({
  ...initialState,

  loadMachineTypes: async () => {
    set({ isLoadingTypes: true, error: null });
    try {
      const services = servicesContainer.getServices();
      const types = await services.getMachineTypesUseCase.execute();
      set({ machineTypes: types, isLoadingTypes: false });
    } catch (error: unknown) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load machine types',
        isLoadingTypes: false,
      });
    }
  },

  validateSerial: async (serialNumber: string) => {
    set({ isValidatingSerial: true, error: null, serialValidation: null });
    try {
      const services = servicesContainer.getServices();
      const result = await services.validateSerialUseCase.execute(serialNumber);
      set({ serialValidation: result, isValidatingSerial: false });
    } catch (error: unknown) {
      set({
        error: error instanceof Error ? error.message : 'Failed to validate serial number',
        isValidatingSerial: false,
      });
    }
  },

  createMachine: async () => {
    const { wizardData } = get();
    set({ isCreating: true, error: null, creationResult: null });
    try {
      const services = servicesContainer.getServices();
      const result = await services.createMachineUseCase.execute({
        machineName:      wizardData.machineName ?? '',
        machineTypeKey:   wizardData.machineTypeKey ?? '',
        actorId:          wizardData.actorId ?? 0,
        machineGroupId:   wizardData.machineGroupId || undefined,
        serialNumber:     wizardData.serialNumber || undefined,
        defaultCredit:    wizardData.defaultCredit || undefined,
        maxCurrentEvse1:  wizardData.maxCurrentEvse1 || undefined,
        maxCurrentEvse2:  wizardData.maxCurrentEvse2 || undefined,
        tariffId:         wizardData.tariffId || undefined,
        defaultLanguage:  wizardData.defaultLanguage || undefined,
        chargerMaxCurrent: wizardData.chargerMaxCurrent || undefined,
        connectorFormat1: wizardData.connectorFormat1 || undefined,
        socketType1:      wizardData.socketType1 || undefined,
        noOfPhases1:      wizardData.noOfPhases1 || undefined,
        chargingType1:    wizardData.chargingType1 || undefined,
        maxPowerKw1:      wizardData.maxPowerKw1 || undefined,
        minCurrentEvse1:  wizardData.minCurrentEvse1 || undefined,
        connectorFormat2:       wizardData.connectorFormat2 || undefined,
        socketType2:            wizardData.socketType2 || undefined,
        noOfPhases2:            wizardData.noOfPhases2 || undefined,
        chargingType2:          wizardData.chargingType2 || undefined,
        maxPowerKw2:            wizardData.maxPowerKw2 || undefined,
        minCurrentEvse2:        wizardData.minCurrentEvse2 || undefined,
        customAttributeValues:  Object.keys(wizardData.customAttributeValues ?? {}).length > 0
                                  ? wizardData.customAttributeValues
                                  : undefined,
      });
      set({ creationResult: result, isCreating: false, currentStep: Step.Result });
    } catch (error: unknown) {
      // Stay on the Review step so the user can correct inputs or retry without losing wizard data.
      set({
        error: error instanceof Error ? error.message : 'Failed to create machine',
        isCreating: false,
      });
    }
  },

  createMachineBulk: async (request: BulkCreateMachineRequest) => {
    set({ isCreatingBulk: true, error: null, bulkCreateResult: null });
    try {
      const services = servicesContainer.getServices();
      const result = await services.createMachineBulkUseCase.execute(request);
      set({ bulkCreateResult: result, isCreatingBulk: false });
    } catch (error: unknown) {
      set({
        error: error instanceof Error ? error.message : 'Failed to create machines',
        isCreatingBulk: false,
      });
    }
  },

  validateMachineConfig: async (machineId: number, machineTypeKey: string) => {
    set({ isValidatingConfig: true, error: null, machineValidationResult: null });
    try {
      const services = servicesContainer.getServices();
      const result = await services.validateMachineUseCase.execute(machineId, machineTypeKey);
      set({ machineValidationResult: result, isValidatingConfig: false });
    } catch (error: unknown) {
      set({
        error: error instanceof Error ? error.message : 'Failed to validate machine configuration',
        isValidatingConfig: false,
      });
    }
  },

  loadMachineGroups: async (actorId: number) => {
    set({ isLoadingMachineGroups: true });
    try {
      const services = servicesContainer.getServices();
      return await services.getMachineGroupsUseCase.execute(actorId);
    } finally {
      set({ isLoadingMachineGroups: false });
    }
  },

  loadActorsForOperator: async (operatorId: number) => {
    set({ isLoadingActors: true });
    try {
      const services = servicesContainer.getServices();
      return await services.getActorsForOperatorUseCase.execute(operatorId);
    } finally {
      set({ isLoadingActors: false });
    }
  },

  loadDevicesForActor: async (actorId: number) => {
    set({ isLoadingDevices: true });
    try {
      const services = servicesContainer.getServices();
      return await services.getDevicesForActorUseCase.execute(actorId);
    } finally {
      set({ isLoadingDevices: false });
    }
  },

  getMachineInfo: async (machineId: number) => {
    set({ isLoadingMachineInfo: true });
    try {
      const services = servicesContainer.getServices();
      return await services.getMachineInfoUseCase.execute(machineId);
    } finally {
      set({ isLoadingMachineInfo: false });
    }
  },

  applyAttributes: async (machineId: number, attributes: ApplyAttributeItem[]) => {
    set({ isApplyingAttributes: true });
    try {
      const services = servicesContainer.getServices();
      return await services.applyAttributesUseCase.execute(machineId, attributes);
    } finally {
      set({ isApplyingAttributes: false });
    }
  },

  loadTypeSettings: async () => {
    set({ isLoadingTypeSettings: true, typeSettingsError: null });
    try {
      const services = servicesContainer.getServices();
      const result = await services.getTypeSettingsUseCase.execute();
      set({ isLoadingTypeSettings: false });
      return result;
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Failed to load type settings';
      set({ isLoadingTypeSettings: false, typeSettingsError: msg });
      throw error;
    }
  },

  saveTypeSettings: async (key: string, settings: SaveTypeSettingsRequest) => {
    set({ isSavingTypeSettings: true, saveTypeSettingsError: null });
    try {
      const services = servicesContainer.getServices();
      const result = await services.saveTypeSettingsUseCase.execute(key, settings);
      set({ isSavingTypeSettings: false });
      return result;
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Failed to save type settings';
      set({ isSavingTypeSettings: false, saveTypeSettingsError: msg });
      throw error;
    }
  },

  addCustomMachineType: async (data: AddCustomMachineTypeRequest) => {
    set({ isAddingCustomType: true });
    try {
      const services = servicesContainer.getServices();
      return await services.addCustomMachineTypeUseCase.execute(data);
    } finally {
      set({ isAddingCustomType: false });
    }
  },

  deleteCustomMachineType: async (key: string) => {
    set({ isDeletingCustomType: true });
    try {
      const services = servicesContainer.getServices();
      return await services.deleteCustomMachineTypeUseCase.execute(key);
    } finally {
      set({ isDeletingCustomType: false });
    }
  },

  loadManufacturers: async () => {
    set({ isLoadingManufacturers: true });
    try {
      const services = servicesContainer.getServices();
      return await services.getMachineSetupManufacturersUseCase.execute();
    } finally {
      set({ isLoadingManufacturers: false });
    }
  },

  createThirdPartyMachine: async (data: CreateThirdPartyMachineRequest) => {
    set({ isCreatingThirdParty: true });
    try {
      const services = servicesContainer.getServices();
      return await services.createThirdPartyMachineUseCase.execute(data);
    } finally {
      set({ isCreatingThirdParty: false });
    }
  },

  setStep: (step: MachineSetupStep) => set({ currentStep: step, error: null }),

  setWizardData: (data: Partial<WizardData>) =>
    set((state) => ({ wizardData: { ...state.wizardData, ...data } })),

  clearSerialValidation: () => set({ serialValidation: null }),

  reset: () => set((state) => ({ ...initialState, machineTypes: state.machineTypes })),
}));

/**
 * Derived selector — true when any async operation is in flight.
 * Zustand store actions are referentially stable so this selector
 * will not cause extra re-renders beyond actual flag changes.
 */
export const useIsLoading = () =>
  useMachineSetupStore(
    (s) =>
      s.isLoadingTypes ||
      s.isValidatingSerial ||
      s.isCreating ||
      s.isCreatingBulk ||
      s.isValidatingConfig ||
      s.isLoadingMachineGroups ||
      s.isLoadingActors ||
      s.isLoadingDevices ||
      s.isLoadingTypeSettings ||
      s.isSavingTypeSettings ||
      s.isAddingCustomType ||
      s.isDeletingCustomType ||
      s.isLoadingManufacturers ||
      s.isLoadingMachineInfo ||
      s.isApplyingAttributes ||
      s.isCreatingThirdParty,
  );
